import { useContext, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { UserContext } from "./UserContext";
import { StreamChatContext } from "./StreamChatProvider";
import Cookies from "js-cookie";
import logo from "../assets/logo/logo.png";

interface MobileMenuProps {
  onOpenAuthModal: () => void;
}

function NavItem({
  to,
  label,
  onClick,
  badge,
  warning,
}: {
  to: string;
  label: string;
  onClick: () => void;
  badge?: number;
  warning?: boolean;
}) {
  const { pathname } = useLocation();
  const isActive = pathname === to;

  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex items-center justify-between px-3 py-3.5 rounded-xl hover:bg-white/5 transition-colors text-sm font-medium text-white"
    >
      <span className="flex items-center gap-2.5">
        {isActive && (
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--primary)] flex-shrink-0" />
        )}
        {label}
      </span>
      <span className="flex items-center gap-2">
        {typeof badge === "number" && (
          <span className="flex h-5 min-w-[1.25rem] px-1 items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold">
            {badge > 9 ? "9+" : badge}
          </span>
        )}
        {warning && (
          <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
        )}
      </span>
    </Link>
  );
}

const Divider = () => (
  <div className="py-3">
    <div className="border-t border-white/10" />
  </div>
);

const CloseIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ArrowIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
  </svg>
);

export default function MobileMenu({ onOpenAuthModal }: MobileMenuProps) {
  const { user, setUser, setProfileFetched, stripeStatus } =
    useContext(UserContext);
  const { unreadCount } = useContext(StreamChatContext);
  const stripeIncomplete = !!(
    user?.isHost &&
    stripeStatus &&
    !stripeStatus.isComplete
  );
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Close on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, location.search]);

  // Lock body scroll when open
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const close = () => setIsOpen(false);

  const logout = () => {
    Cookies.remove("authToken");
    setUser(null);
    setProfileFetched(false);
    close();
  };

  const hasNotification =
    user && (unreadCount > 0 || stripeIncomplete);

  const HeaderBar = ({ onClose }: { onClose: () => void }) => (
    <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10 flex-shrink-0">
      <Link to="/" onClick={onClose}>
        <img src={logo} alt="AirArrange Logo" className="h-10 w-auto" />
      </Link>
      <button
        onClick={onClose}
        className="flex items-center justify-center w-10 h-10 rounded-full text-white hover:bg-white/10 transition-colors"
        aria-label="Close menu"
      >
        <CloseIcon />
      </button>
    </div>
  );

  return (
    <div className="md:hidden">
      {/* ── Compact header bar (visible when menu is closed) ── */}
      <div className="flex items-center justify-between px-4 py-2.5">
        <Link to="/" onClick={close}>
          <img src={logo} alt="AirArrange Logo" className="h-10 w-auto" />
        </Link>

        <button
          onClick={() => setIsOpen(true)}
          className="relative flex items-center justify-center w-10 h-10 rounded-full text-white hover:bg-white/10 transition-colors"
          aria-label="Open navigation menu"
        >
          {hasNotification && (
            <span className="absolute top-1.5 right-1.5 flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
            </span>
          )}
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
      </div>

      {/* ── Full-screen overlay ── */}
      {isOpen && (
        <div className="fixed inset-0 z-50 bg-[var(--secondary)] flex flex-col">
          <HeaderBar onClose={close} />

          <div className="flex-1 overflow-y-auto">
            <nav className="px-4 pt-5 pb-12 space-y-0.5">

              {/* Navigation */}
              <p className="text-white/40 text-[10px] uppercase tracking-widest font-semibold px-3 pb-2">
                Navigation
              </p>
              <NavItem to="/workspaces" label="Workspaces" onClick={close} />
              <NavItem to="/aboutUs" label="About Us" onClick={close} />
              <NavItem to="/solutions" label="Solutions" onClick={close} />

              <Divider />

              {/* List your Workspace CTA */}
              {user ? (
                <Link
                  to={location.pathname === "/host" ? "/account" : "/host"}
                  onClick={close}
                  className="flex items-center justify-between w-full px-3 py-3.5 bg-[var(--primary)] text-[var(--secondary)] font-semibold rounded-xl text-sm"
                >
                  {location.pathname === "/host" ? "Switch to experience" : "Switch to Hosting"}
                  <ArrowIcon />
                </Link>
              ) : (
                <button
                  onClick={() => { onOpenAuthModal(); close(); }}
                  className="flex items-center justify-between w-full px-3 py-3.5 bg-[var(--primary)] text-[var(--secondary)] font-semibold rounded-xl text-sm"
                >
                  List your Workspace
                  <ArrowIcon />
                </button>
              )}

              <Divider />

              {/* Account section */}
              {user ? (
                <>
                  <p className="text-white/40 text-[10px] uppercase tracking-widest font-semibold px-3 pb-2">
                    Account
                  </p>

                  {/* User identity card */}
                  <div className="flex items-center gap-3 px-3 py-3 mb-1">
                    <div className="w-9 h-9 rounded-full bg-[var(--primary)] flex items-center justify-center text-[var(--secondary)] font-bold text-sm flex-shrink-0">
                      {user.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-white font-semibold text-sm truncate">
                        {user.name}
                      </p>
                      <p className="text-white/50 text-xs truncate">
                        {user.email}
                      </p>
                    </div>
                  </div>

                  {user.isHost ? (
                    <>
                      <NavItem
                        to="/account?section=profile"
                        label="Account"
                        onClick={close}
                        warning={stripeIncomplete}
                      />
                      <NavItem
                        to="/host?section=bookings"
                        label="My Bookings"
                        onClick={close}
                      />
                      <NavItem
                        to="/host?section=listings"
                        label="My Workspaces"
                        onClick={close}
                      />
                      <NavItem
                        to="/messages"
                        label="Messages"
                        onClick={close}
                        badge={unreadCount > 0 ? unreadCount : undefined}
                      />
                      <NavItem
                        to="/helpCentre"
                        label="Help Centre"
                        onClick={close}
                      />
                      <NavItem
                        to="/contactUs"
                        label="Contact Us"
                        onClick={close}
                      />
                    </>
                  ) : (
                    <>
                      <NavItem
                        to="/account?section=profile"
                        label="Account"
                        onClick={close}
                      />
                      <NavItem
                        to="/account?section=upcomingBookings"
                        label="My Bookings"
                        onClick={close}
                      />
                      <NavItem
                        to="/account?section=pastBookings"
                        label="Past Bookings"
                        onClick={close}
                      />
                      <NavItem
                        to="/messages"
                        label="Messages"
                        onClick={close}
                        badge={unreadCount > 0 ? unreadCount : undefined}
                      />
                      <NavItem
                        to="/helpCentre"
                        label="Help Centre"
                        onClick={close}
                      />
                      <NavItem
                        to="/contactUs"
                        label="Contact Us"
                        onClick={close}
                      />
                    </>
                  )}

                  <Divider />

                  <button
                    onClick={logout}
                    className="w-full flex items-center gap-2.5 px-3 py-3.5 text-red-400 hover:bg-white/5 rounded-xl transition-colors text-sm font-medium"
                  >
                    <svg
                      className="w-4 h-4 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                      />
                    </svg>
                    Log Out
                  </button>
                </>
              ) : (
                <>
                  <p className="text-white/40 text-[10px] uppercase tracking-widest font-semibold px-3 pb-2">
                    Account
                  </p>
                  <button
                    onClick={() => { onOpenAuthModal(); close(); }}
                    className="flex items-center justify-between w-full px-3 py-3.5 text-white hover:bg-white/5 rounded-xl transition-colors text-sm font-medium"
                  >
                    Log in or sign up
                    <ArrowIcon />
                  </button>
                  <NavItem to="/contact" label="Contact Us" onClick={close} />
                  <NavItem to="/help" label="Help Center" onClick={close} />
                </>
              )}
            </nav>
          </div>
        </div>
      )}
    </div>
  );
}
